/**
 * Organizations List Page
 *
 * Lists all organizations (CRAs and Banks) with management capabilities
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAllOrganizations, updateOrganization } from '../../../services/firestore';
import type { Organization, OrgStatus } from '../../../services/firestore';
import { getRatedInstitutions } from '../../../services/institutionService';
import type { RatedInstitution } from '../../../types/institution';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './OrganizationsList.css';

interface EditModalState {
  isOpen: boolean;
  org: Organization | null;
}

interface Filters {
  type: string;
  status: string;
  search: string;
}

export const OrganizationsList: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    type: '',
    status: '',
    search: '',
  });
  const [editModal, setEditModal] = useState<EditModalState>({ isOpen: false, org: null });
  const [editData, setEditData] = useState<{ status: OrgStatus; institutions_access: string[] }>({
    status: 'active',
    institutions_access: [],
  });
  const [processing, setProcessing] = useState(false);
  const [availableInstitutions, setAvailableInstitutions] = useState<RatedInstitution[]>([]);

  useEffect(() => {
    fetchOrganizations();
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    try {
      const institutions = await getRatedInstitutions();
      setAvailableInstitutions(institutions);
    } catch (err) {
      console.error('Error fetching institutions:', err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const orgs = await getAllOrganizations();
      setOrganizations(orgs);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (org: Organization) => {
    setEditModal({ isOpen: true, org });
    setEditData({
      status: org.status,
      institutions_access: org.institutions_access || [],
    });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, org: null });
  };

  const handleUpdateOrg = async () => {
    if (!editModal.org) return;

    try {
      setProcessing(true);
      await updateOrganization(editModal.org.id!, {
        status: editData.status,
        institutions_access: editData.institutions_access,
      });
      await fetchOrganizations();
      closeEditModal();
    } catch (err) {
      console.error('Error updating organization:', err);
      setError('Failed to update organization');
    } finally {
      setProcessing(false);
    }
  };

  const toggleInstitution = (institution: string) => {
    setEditData(prev => ({
      ...prev,
      institutions_access: prev.institutions_access.includes(institution)
        ? prev.institutions_access.filter(i => i !== institution)
        : [...prev.institutions_access, institution],
    }));
  };

  const getParentName = (parentId: string | null | undefined) => {
    if (!parentId) return '-';
    const parent = organizations.find(o => o.id === parentId);
    return parent?.name || parentId;
  };

  // Filter organizations
  const filteredOrgs = organizations.filter(org => {
    if (filters.type && org.type !== filters.type) return false;
    if (filters.status && org.status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!org.name.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="orgs-list-page">
        <div className="page-header">
          <h1>Organizations</h1>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="orgs-list-page">
      <div className="page-header">
        <h1>Organizations</h1>
        <Link to="/admin/organizations/create" className="create-btn">
          + Create Organization
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search by name..."
          value={filters.search}
          onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="search-input"
        />

        <select
          value={filters.type}
          onChange={e => setFilters(prev => ({ ...prev, type: e.target.value }))}
        >
          <option value="">All Types</option>
          <option value="platform">Platform</option>
          <option value="cra">CRA</option>
          <option value="bank">Bank</option>
        </select>

        <select
          value={filters.status}
          onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>

        <button
          className="clear-filters-btn"
          onClick={() => setFilters({ type: '', status: '', search: '' })}
        >
          Clear
        </button>
      </div>

      <div className="results-count">
        Showing {filteredOrgs.length} of {organizations.length} organizations
      </div>

      {/* Organizations Table */}
      <div className="orgs-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Parent</th>
              <th>Status</th>
              <th>Institutions</th>
              <th>Licenses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map(org => (
              <tr key={org.id}>
                <td className="name-cell">{org.name}</td>
                <td>
                  <span className={`type-badge ${org.type}`}>
                    {org.type.toUpperCase()}
                  </span>
                </td>
                <td>{getParentName(org.parent_org_id)}</td>
                <td>
                  <span className={`status-badge ${org.status}`}>
                    {org.status}
                  </span>
                </td>
                <td>{org.institutions_access?.length || 0}</td>
                <td>
                  {org.licenses ? (
                    <span className="license-info">
                      {org.licenses.analyst_seats_used || 0}/{org.licenses.analyst_seats || 0} seats
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <button
                    className="edit-btn"
                    onClick={() => openEditModal(org)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredOrgs.length === 0 && (
          <div className="no-results">
            No organizations found matching your filters.
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && editModal.org && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <h2>Edit Organization</h2>
            <p className="modal-org-name">{editModal.org.name}</p>

            <div className="form-group">
              <label htmlFor="edit-status">Status</label>
              <select
                id="edit-status"
                value={editData.status}
                onChange={e => setEditData(prev => ({ ...prev, status: e.target.value as OrgStatus }))}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div className="form-group">
              <label>Institutions Access</label>
              <div className="institutions-grid">
                {availableInstitutions.map(inst => (
                  <label key={inst.institution_id} className="institution-checkbox">
                    <input
                      type="checkbox"
                      checked={editData.institutions_access.includes(inst.institution_id)}
                      onChange={() => toggleInstitution(inst.institution_id)}
                    />
                    <span>{inst.institution_name}</span>
                  </label>
                ))}
              </div>
              <p className="help-text">
                {editData.institutions_access.length} of {availableInstitutions.length} institutions selected
              </p>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeEditModal} disabled={processing}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleUpdateOrg}
                disabled={processing}
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsList;
